import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  BarChart3,
  Brain,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  FileText,
  GitBranch,
  Globe,
  Inbox,
  Lock,
  Phone,
  Scale,
  Send,
  Settings,
  Shield,
  TrendingUp,
  UserPlus,
  Users,
  X,
  CheckCheck,
} from 'lucide-react'
import clsx from 'clsx'
import { useNotificationStore, type Notification } from '@/store/notificationStore'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  AlertTriangle,
  BarChart3,
  Brain,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  FileText,
  GitBranch,
  Globe,
  Inbox,
  Lock,
  Phone,
  Scale,
  Send,
  Settings,
  Shield,
  TrendingUp,
  UserPlus,
  Users,
}

const FILTER_TABS: { label: string; value: Notification['type'] | null }[] = [
  { label: 'All', value: null },
  { label: 'Deals', value: 'deal' },
  { label: 'Triggers', value: 'trigger' },
  { label: 'Committee', value: 'committee' },
  { label: 'System', value: 'system' },
  { label: 'Reports', value: 'report' },
  { label: 'CRM', value: 'crm' },
  { label: 'Market', value: 'market' },
  { label: 'AI', value: 'ai' },
]

const PRIORITY_COLORS: Record<Notification['priority'], string> = {
  high: 'bg-red-500',
  medium: 'bg-amber-400',
  low: 'bg-emerald-500',
}

function formatRelativeTime(iso: string): string {
  const now = Date.now()
  const ts = new Date(iso).getTime()
  const diffMs = now - ts
  const diffMin = Math.floor(diffMs / 60_000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin} min ago`

  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`

  const date = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
}

type TimeBucket = 'Today' | 'Yesterday' | 'This Week' | 'Older'

function getTimeBucket(iso: string): TimeBucket {
  const now = new Date()
  const ts = new Date(iso)
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const weekAgo = new Date(today)
  weekAgo.setDate(weekAgo.getDate() - 7)

  if (ts >= today) return 'Today'
  if (ts >= yesterday) return 'Yesterday'
  if (ts >= weekAgo) return 'This Week'
  return 'Older'
}

function groupByBucket(notifications: Notification[]): [TimeBucket, Notification[]][] {
  const order: TimeBucket[] = ['Today', 'Yesterday', 'This Week', 'Older']
  const groups: Record<TimeBucket, Notification[]> = {
    Today: [],
    Yesterday: [],
    'This Week': [],
    Older: [],
  }

  for (const n of notifications) {
    groups[getTimeBucket(n.timestamp)].push(n)
  }

  return order.filter((b) => groups[b].length > 0).map((b) => [b, groups[b]])
}

function NotificationItem({
  notification,
  onRead,
  onDismiss,
  onOpen,
}: {
  notification: Notification
  onRead: (id: string) => void
  onDismiss: (id: string) => void
  /** Go to what the notification is about. */
  onOpen: (url: string) => void
}) {
  const IconComponent = ICON_MAP[notification.icon || ''] || FileText

  // A notification that names a place opens it (T-42, 2 Nov 2026: "Export waiting
  // for your approval" could not be clicked through to the request). Reading it
  // still marks it read.
  const open = () => {
    onRead(notification.id)
    if (notification.actionUrl) onOpen(notification.actionUrl)
  }

  return (
    <div
      role={notification.actionUrl ? 'link' : undefined}
      tabIndex={notification.actionUrl ? 0 : undefined}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && notification.actionUrl) open()
      }}
      onClick={open}
      className={clsx(
        'group relative flex cursor-pointer gap-2.5 rounded-lg border px-3 py-2.5 transition-all duration-150',
        notification.read
          ? 'border-transparent hover:border-amber-400/5 hover:bg-white/[0.01]'
          : 'border-l-2 border-l-amber-400/40 border-t-transparent border-r-transparent border-b-transparent bg-amber-400/[0.03] hover:bg-amber-400/[0.06]'
      )}
    >
      {/* Priority dot + icon */}
      <div className="flex flex-col items-center gap-1.5 pt-0.5">
        <span className={clsx('h-1.5 w-1.5 flex-shrink-0 rounded-full', PRIORITY_COLORS[notification.priority])} />
        <IconComponent size={13} className="text-stone-500" />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className={clsx('text-[12px] leading-snug', notification.read ? 'font-normal text-stone-300' : 'font-semibold text-stone-100')}>
          {notification.title}
        </div>
        <div className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-stone-500">
          {notification.message}
        </div>
        <div className="mt-1 text-[10px] font-mono text-stone-600">
          {formatRelativeTime(notification.timestamp)}
        </div>
      </div>

      {/* Dismiss */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDismiss(notification.id)
        }}
        className="absolute right-2 top-2 hidden rounded p-0.5 text-stone-600 transition-colors hover:text-stone-300 group-hover:block"
      >
        <X size={11} />
      </button>
    </div>
  )
}

export default function NotificationPanel() {
  const {
    notifications,
    showPanel,
    togglePanel,
    markAsRead,
    markAllRead,
    dismissNotification,
    filterType,
    setFilterType,
    unreadCount,
  } = useNotificationStore()

  const panelRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const openTarget = useCallback(
    (url: string) => {
      if (showPanel) togglePanel()
      navigate(url)
    },
    [navigate, showPanel, togglePanel],
  )

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        // Don't close if clicking the bell button (it toggles via its own handler)
        const target = e.target as HTMLElement
        if (target.closest('[data-notification-bell]')) return
        if (showPanel) togglePanel()
      }
    },
    [showPanel, togglePanel]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [handleClickOutside])

  const filtered = filterType ? notifications.filter((n) => n.type === filterType) : notifications
  const sorted = [...filtered].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  const grouped = groupByBucket(sorted)

  return (
    <>
      {/* Backdrop */}
      <div
        className={clsx(
          'fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-200',
          showPanel ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={clsx(
          'fixed right-0 top-0 z-50 flex h-full w-[380px] flex-col border-l border-amber-400/15 bg-[#090909]/[0.97] backdrop-blur-2xl transition-transform duration-300 ease-out',
          showPanel ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-amber-400/10 px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="text-[13px] font-semibold tracking-wide text-stone-100">NOTIFICATIONS</h2>
            {unreadCount > 0 && (
              <span className="rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[10px] font-mono font-bold text-amber-300">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={markAllRead}
              className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-stone-400 transition-colors hover:bg-amber-400/[0.06] hover:text-amber-300"
              title="Mark all as read"
            >
              <CheckCheck size={11} />
              <span>Mark All</span>
            </button>
            <button
              onClick={togglePanel}
              className="rounded-md p-1 text-stone-500 transition-colors hover:text-stone-200"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-shrink-0 gap-1 overflow-x-auto border-b border-amber-400/10 px-3 py-2 scrollbar-thin">
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.label}
              onClick={() => setFilterType(tab.value)}
              className={clsx(
                'flex-shrink-0 rounded-md px-2 py-1 text-[10px] font-medium transition-all duration-150',
                filterType === tab.value
                  ? 'border border-amber-400/20 bg-amber-400/[0.08] text-amber-300'
                  : 'border border-transparent text-stone-500 hover:bg-white/[0.03] hover:text-stone-300'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Notification List */}
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {grouped.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <CheckCircle size={28} className="mb-2 text-stone-700" />
              <p className="text-[12px] text-stone-500">No notifications</p>
              <p className="mt-1 max-w-[260px] text-[10.5px] leading-relaxed text-stone-600">
                Live today: open triggers on the active book. Stage moves, committee
                and report events are not delivered here yet.
              </p>
            </div>
          )}

          {grouped.map(([bucket, items]) => (
            <div key={bucket} className="mb-3">
              {/* Time bucket header */}
              <div className="mb-1.5 flex items-center gap-2 px-2">
                <div className="h-px flex-1 bg-amber-400/8" />
                <span className="text-[9px] font-mono uppercase tracking-[0.2em] text-stone-600">{bucket}</span>
                <div className="h-px flex-1 bg-amber-400/8" />
              </div>

              <div className="space-y-0.5">
                {items.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onRead={markAsRead}
                    onDismiss={dismissNotification}
                    onOpen={openTarget}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* There is no notifications page: the "View All Notifications" button that
            stood here did nothing when pressed, and a control that does nothing is
            not shown. */}
      </div>
    </>
  )
}
